import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { OurSquareLogo } from '../../components/brand/Logo';
import { toast } from 'sonner';
import { 
  FileText, CheckCircle, Clock, PenTool, AlertTriangle, User, Download
} from 'lucide-react';

export default function ResidentDocumentsPage() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signOpen, setSignOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [signForm, setSignForm] = useState({ signature_text: '', signature_date: '' });
  const [residentId, setResidentId] = useState(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      // First get the resident record for this user
      const housesRes = await api.get('/housing/entity/our_square/houses');
      const houses = housesRes.data || [];
      
      // Find the resident record for the current user
      let foundResidentId = null;
      for (const house of houses) {
        const resident = (house.residents || []).find(r => r.user_id === user.id);
        if (resident) {
          foundResidentId = resident.id;
          break;
        }
      }

      if (foundResidentId) {
        setResidentId(foundResidentId);
        const res = await api.get(`/housing-documents/resident/${foundResidentId}`);
        setData(res.data);
      } else {
        // Show empty state with document types
        const typesRes = await api.get('/housing-documents/types');
        setData({
          documents: [],
          completion_status: typesRes.data.map(t => ({
            document_type: t.key,
            name: t.name,
            requires_signature: t.requires_signature,
            status: 'pending',
          })),
          total_required: typesRes.data.filter(t => t.requires_signature).length,
          total_signed: 0,
        });
      }
    } catch (e) { 
      // Show empty state
      try {
        const typesRes = await api.get('/housing-documents/types');
        setData({
          documents: [],
          completion_status: typesRes.data.map(t => ({
            document_type: t.key,
            name: t.name,
            requires_signature: t.requires_signature,
            status: 'pending',
          })),
          total_required: typesRes.data.filter(t => t.requires_signature).length,
          total_signed: 0,
        });
      } catch (e2) { console.error(e2); }
    }
    setLoading(false);
  };

  const openSign = (doc) => {
    setSelectedDoc(doc);
    setSignForm({ signature_text: '', signature_date: new Date().toISOString().split('T')[0] });
    setSignOpen(true);
  };

  const signDocument = async () => {
    if (!signForm.signature_text) return toast.error('Please enter your full name as signature');
    try {
      await api.post(`/housing-documents/${selectedDoc.id}/sign`, signForm);
      toast.success('Document signed successfully!');
      setSignOpen(false);
      setSelectedDoc(null);
      loadDocuments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to sign'); }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;
  if (!data) return <div className="text-center py-12 text-muted-foreground">Unable to load documents</div>;

  const progress = data.total_required > 0 ? Math.round((data.total_signed / data.total_required) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="resident-documents-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <OurSquareLogo size="sm" className="h-12 w-auto" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Documents</h1>
          <p className="text-sm text-muted-foreground">View and sign your housing agreements</p>
        </div>
      </div>

      {/* Progress Overview */}
      <Card className="border-0 shadow-sm rounded-xl bg-gradient-to-r from-cyan-50 to-teal-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-4xl font-bold text-cyan-700">{progress}%</p>
              <p className="text-sm text-muted-foreground">Documents Complete</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-slate-900">{data.total_signed}/{data.total_required}</p>
              <p className="text-sm text-muted-foreground">Required Documents Signed</p>
            </div>
          </div>
          <Progress value={progress} className="h-4" />
          
          {progress === 100 && (
            <div className="mt-4 p-3 bg-emerald-100 rounded-lg text-center">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
              <p className="font-semibold text-emerald-800">All required documents are signed!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Signatures Alert */}
      {data.documents.filter(d => d.status === 'pending' && d.requires_signature).length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-amber-400">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <PenTool size={20} className="text-amber-600" />
              <p className="font-medium text-amber-800">
                You have {data.documents.filter(d => d.status === 'pending' && d.requires_signature).length} document(s) awaiting your signature
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Checklist */}
      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Document Checklist</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {data.completion_status.map(item => {
              const isSigned = item.status === 'signed';
              const isPending = item.status === 'pending' && item.requires_signature;
              
              return (
                <div key={item.document_type} className={`flex items-center justify-between p-4 rounded-xl ${isSigned ? 'bg-emerald-50' : isPending ? 'bg-amber-50' : 'bg-slate-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSigned ? 'bg-emerald-500 text-white' : isPending ? 'bg-amber-400 text-white' : 'bg-slate-200 text-slate-500'}`}>
                      {isSigned ? <CheckCircle size={20} /> : <FileText size={20} />}
                    </div>
                    <div>
                      <p className={`font-medium ${isSigned ? 'text-emerald-700' : ''}`}>{item.name}</p>
                      {item.requires_signature && !isSigned && (
                        <p className="text-xs text-amber-600">Signature required</p>
                      )}
                      {!item.requires_signature && (
                        <p className="text-xs text-slate-500">No signature needed</p>
                      )}
                    </div>
                  </div>
                  {isSigned ? (
                    <Badge className="bg-emerald-100 text-emerald-700">Signed</Badge>
                  ) : isPending ? (
                    <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600">Optional</Badge>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Your Documents */}
      {data.documents.length > 0 && (
        <Card className="border-0 shadow-sm rounded-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Your Documents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.documents.map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:bg-slate-50">
                  <div className="flex items-center gap-3">
                    <FileText size={24} className={doc.status === 'signed' ? 'text-emerald-600' : 'text-slate-400'} />
                    <div>
                      <p className="font-medium">{doc.title || doc.document_type_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Created {new Date(doc.created_at).toLocaleDateString()}
                        {doc.signed_at && ` • Signed ${new Date(doc.signed_at).toLocaleDateString()}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.status === 'signed' ? (
                      <Badge className="bg-emerald-100 text-emerald-700">Signed</Badge>
                    ) : doc.requires_signature ? (
                      <Button size="sm" onClick={() => openSign(doc)} className="rounded-full bg-cyan-600 hover:bg-cyan-700 text-white gap-1">
                        <PenTool size={14} /> Sign Now
                      </Button>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-600">Complete</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sign Dialog */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Sign Document</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="p-4 bg-cyan-50 rounded-lg">
              <p className="font-medium text-cyan-800">{selectedDoc?.title || selectedDoc?.document_type_name}</p>
              <p className="text-sm text-cyan-600 mt-2">
                By signing this document, you acknowledge that you have read and understand all terms and conditions. Your electronic signature is legally binding.
              </p>
            </div>
            
            <div>
              <Label>Your Full Legal Name (as signature) *</Label>
              <Input 
                value={signForm.signature_text} 
                onChange={e => setSignForm({ ...signForm, signature_text: e.target.value })} 
                placeholder="Type your full legal name exactly as shown on your ID"
                className="mt-1 text-lg"
              />
            </div>
            
            <div>
              <Label>Date</Label>
              <Input 
                type="date" 
                value={signForm.signature_date} 
                onChange={e => setSignForm({ ...signForm, signature_date: e.target.value })} 
                className="mt-1"
              />
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-800 font-medium">Electronic Signature Agreement</p>
              <p className="text-xs text-amber-700 mt-1">
                By clicking "Sign Document", I agree that my typed name above constitutes my electronic signature and has the same legal effect as a handwritten signature. I understand that this signature cannot be withdrawn once submitted.
              </p>
            </div>
            
            <Button onClick={signDocument} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full gap-2">
              <PenTool size={16} /> Sign Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
